// ── CODE HISTORIQUE de « Dans le Bureau » ────────────────────────────────────
// Extrait TEL QUEL du single-file index.html le 2026-08-06, sans réécriture : un
// déplacement se vérifie, une réécriture non. Les six features (liste, analyse,
// alertes, collecteurs, fournisseurs, coût produit) sont inchangées.
//
// Ce qui a changé : les fonctions qui existaient ici en double avec un défaut
// (formatage, tri, session, client HTTP) ont été RETIRÉES et sont désormais
// importées de src/lib/, où elles sont corrigées et testées. Il fallait que ce
// fichier devienne un module pour ça : en script classique, ses appels internes
// restaient liés aux anciennes définitions lexicales, et les écraser depuis
// l'extérieur n'aurait rien changé.
//
// Les fonctions citées dans les attributs HTML `onclick=` sont réexposées sur
// window en fin de fichier — dette assumée et tracée, à retirer feature par
// feature au fur et à mesure qu'on y touche.

import { eur, eur2, fdate, moisLbl, pctv, escH, echClass, deltaPts } from "./lib/format.js";
import { pastilleNature, ventilation } from "./lib/nature.js";
import { norm as _norm, sortKey, accentAt, buildRanges } from "./lib/tri.js";
import { statutCollecteur, sousTitreCollecteur, actionCollecteur, separerSources } from "./lib/collecteur-statut.js";
import { api } from "./lib/api.js";
import { seDeconnecter, allerVersEtikid } from "./lib/etikid.js";
import { ongletDefaut, panesVisibles, filtresVisibles, triDeLOnglet } from "./lib/onglets.js";
import { prendreLien, oublierLien, resoudreFournisseur } from "./lib/lien.js";
import { synchroniserNavigation } from "./features/navigation.js";
import { rendreEtiquetage, chargerEtiquetage, chargerCodesGroupe } from "./features/etiquetage.js";
import { boutonGarder } from "./features/presse-papier-ui.js";
import { entreeAlerte, entreeEcheance, entreeFournisseur, entreePrix }
  from "./lib/presse-papier-entrees.js";
import { rendreAccueil, chargerAccueil } from "./features/accueil.js";
import { rendrePilotage, chargerPilotage } from "./features/pilotage.js";
import { rendreFrais, chargerFrais } from "./features/frais.js";
import { rendreRemiseGrossiste, chargerRemiseGrossiste } from "./features/remise-grossiste.js";

const PAGE = 200;
const SRC_LABEL = {digipharmacie:"DigiPharmacie", alliance:"Alliance", gsa:"GSA", mail_etik:"Mail", mail_pharmavance:"Mail Pharmavance", sy:"SY"};
let TAB = ongletDefaut().id;
// Le labo d'un lien direct (lib/lien.js), en attente du menu « Fournisseur » pour être rapproché.
let LIEN_LABO = null;
let SORT = "date";          // date | echeance | fournisseur | ht | ttc
let OFFSET = 0;
let LOADED = 0;             // nb de lignes affichées

function cell(text, cls){ const td=document.createElement("td"); if(cls)td.className=cls; td.textContent=text; return td; }
function emptyRow(text){
  const tr=document.createElement("tr"); const td=document.createElement("td");
  td.colSpan=10; td.className="empty"; td.textContent=text; tr.appendChild(td); return tr;
}

// ---- « Combien je paie » : prix de revient (UG inclus) + marge par produit ----
// Rendu via innerHTML avec échappement systématique (escH) de TOUTE valeur dynamique.
let coutTimer=null;
function debouncedCout(){ clearTimeout(coutTimer); coutTimer=setTimeout(searchCout,300); }
async function searchCout(){
  const q=document.getElementById("coutSearch").value.trim();
  const list=document.getElementById("coutList");
  document.getElementById("coutCard").innerHTML="";
  if(q.length<2){ list.innerHTML=""; return; }
  list.innerHTML='<div class="muted">Recherche…</div>';
  try{
    const {produits}=await api("produit_search",{query:q});
    if(!produits||!produits.length){ list.innerHTML='<div class="muted">Aucun produit dans l\'historique d\'achat.</div>'; return; }
    list.innerHTML=produits.map(p=>
      `<div onclick="loadCout('${escH(p.cip13)}')" style="padding:8px 10px;border:1px solid var(--line);border-radius:8px;margin-bottom:6px;cursor:pointer;display:flex;justify-content:space-between;gap:10px">
         <span>${escH(p.designation||"—")} <span class="muted" style="font-size:12px">${escH(p.cip13)}</span></span>
         <span class="muted" style="font-size:12px;white-space:nowrap">${p.nb_livraisons} livr.${p.derniere_livraison?" · "+escH(p.derniere_livraison):""}</span>
       </div>`).join("");
  }catch(e){ list.innerHTML='<div class="err">'+escH(e.message)+'</div>'; }
}
async function loadCout(cip){
  const card=document.getElementById("coutCard");
  card.innerHTML='<div class="muted">Chargement…</div>';
  try{
    const {prix_achat:pa, marge:mg}=await api("produit_cout",{cip13:cip});
    card.innerHTML=coutCardHtml(pa,mg);
    // Le bouton est GREFFÉ après coup, jamais écrit dans le gabarit : un bouton
    // sorti d'une chaîne HTML n'a pas de gestionnaire, il serait décoratif.
    const emplacement = card.querySelector(".cout-garder");
    if (emplacement && pa) emplacement.appendChild(boutonGarder(entreePrix(pa, mg)));
  }catch(e){ card.innerHTML='<div class="err">'+escH(e.message)+'</div>'; }
}

function coutCardHtml(pa,mg){
  if(!pa) return '<div class="muted">Aucun historique d\'achat.</div>';
  const ug=Number(pa.derniere_livraison_ug||0), ugTxt=ug>0?` + <b>${ug} UG</b>`:"";
  const mh=(mg&&mg.marge_ht!=null)?Number(mg.marge_ht):null;
  const mCol=mh==null?"var(--muted)":(mh>=0?"var(--ok)":"var(--late)");
  const pvSrc=mg&&mg.pv_source==="referentiel_ppttc"?"PPTTC référentiel (remboursable — marge brute indicative)"
            :mg&&mg.pv_source==="offisante_mon_prix"?"votre prix pratiqué (Offisanté)":"prix de vente inconnu";
  const cell=(l,v,strong)=>`<div style="flex:1;min-width:140px"><div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:.04em">${escH(l)}</div><div style="font-size:${strong?"20px":"16px"};font-weight:${strong?"700":"600"}">${v}</div></div>`;
  return `
  <div style="border:1px solid var(--line);border-radius:12px;padding:16px;background:var(--card)">
    <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:12px">
      <div style="font-weight:700;flex:1 1 auto">${escH(pa.designation||"—")} <span class="muted" style="font-size:12px;font-weight:400">${escH(pa.cip13)}</span></div>
      <div class="cout-garder" style="flex:0 0 auto"></div>
    </div>
    <div style="display:flex;gap:16px;flex-wrap:wrap">
      ${cell("Prix catalogue",eur2(pa.prix_catalogue))}
      ${cell("Dernier remisé",eur2(pa.prix_remise_dernier)+(pa.remise_pct_dernier!=null?` <span class="muted" style="font-size:12px">-${pctv(pa.remise_pct_dernier)}</span>`:""))}
      ${cell("Revient dernière livr.",eur2(pa.prix_revient_livraison))}
      ${cell("Revient pondéré "+new Date().getFullYear(),eur2(pa.prix_revient_pondere_ytd),true)}
    </div>
    <div class="muted" style="font-size:12px;margin-top:10px;border-top:1px solid var(--line);padding-top:10px">
      Dernière livraison ${pa.derniere_livraison_date?escH(pa.derniere_livraison_date):"—"}${pa.derniere_livraison_labo?" · "+escH(pa.derniere_livraison_labo):""} :
      <b>${Number(pa.derniere_livraison_qte||0)}</b> commandé${ugTxt}. ${Number(pa.nb_livraisons_ytd||0)} livraison(s) ${new Date().getFullYear()} (${Number(pa.qte_recue_ytd||0)+Number(pa.ug_recue_ytd||0)} unités reçues).
    </div>
    <div style="margin-top:12px;padding:10px 12px;border-radius:8px;background:var(--bg);display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
      <span style="font-weight:600">Marge ${mg&&mg.pv_ttc!=null?`<span class="muted" style="font-weight:400">PV ${eur2(mg.pv_ttc)} TTC${mg.pv_ht!=null?" · "+eur2(mg.pv_ht)+" HT":""}</span>`:""}</span>
      <span style="font-weight:700;font-size:18px;color:${mCol}">${mh!=null?eur2(mh):"—"}${mg&&mg.marge_pct!=null?` (${pctv(mg.marge_pct)})`:""}</span>
    </div>
    <div class="muted" style="font-size:11px;margin-top:4px">Prix de vente : ${escH(pvSrc)}</div>
  </div>`;
}

// Le flux de connexion EtikID vivait ici ; il est passé dans src/lib/etikid.js,
// où quatre défauts qui enfermaient l'utilisateur DEHORS sont corrigés (boucle
// infinie sans garde-fou, impasse au rechargement, connexions concurrentes,
// absence de délai d'attente). Sa logique décidable est testée dans session.js.

function showApp(){
  document.getElementById("login").style.display="none";
  document.getElementById("app").style.display="block";
  // Lien direct (?onglet=…&labo=…) : pris par main.js avant le gate, appliqué ici une seule fois.
  const lien = prendreLien();
  // Le labo ne vaut que sur une LISTE de factures (« Échéances à venir », « Toutes ») — les seules qui
  // remplissent le menu Fournisseur : ailleurs il resterait en attente et filtrerait plus tard une
  // liste que personne n'a demandée.
  if (lien) { TAB = lien.onglet; LIEN_LABO = ["ech", "all"].includes(lien.onglet) ? (lien.labo ?? null) : null; oublierLien(); }
  setTab(TAB);
}

/**
 * Applique le labo d'un lien direct, une fois connus les fournisseurs des factures. Rend true si un
 * filtre a été posé (il faut alors recharger). Dans tous les cas, l'écran DIT ce qui a été fait.
 */
function appliquerLaboDuLien(texte, labos){
  const info = document.getElementById("lienInfo");
  const dire = (html) => { info.innerHTML = html; info.style.display = ""; };
  const r = resoudreFournisseur(labos.map((l) => l.fournisseur), texte);
  if (r && r.fournisseur) {
    document.getElementById("fournisseur").value = r.fournisseur;
    dire(`Filtré sur <b>${escH(r.fournisseur)}</b> (lien « ${escH(texte)} »). Fournisseur « Tous » pour tout revoir.`);
    return true;
  }
  if (r && r.plusieurs) {
    document.getElementById("q").value = texte;
    dire(`${r.plusieurs.length} fournisseurs correspondent à « ${escH(texte)} » (${r.plusieurs.slice(0, 4).map(escH).join(", ")}${r.plusieurs.length > 4 ? "…" : ""}) : recherche « ${escH(texte)} ».`);
    return true;
  }
  dire(`Aucune facture d'un fournisseur « ${escH(texte)} » : la liste n'est pas filtrée.`);
  return false;
}

/**
 * Bascule d'onglet — pilotée par la TABLE (lib/onglets.js), plus par trois
 * listes parallèles à tenir synchronisées à la main.
 */
function setTab(t){
  TAB = t;
  // Le bandeau d'un lien direct décrit l'écran d'arrivée : il s'efface dès qu'on change d'écran.
  const lienInfo = document.getElementById("lienInfo");
  if (lienInfo) lienInfo.style.display = "none";

  // Les deux barres se réalignent sur l'onglet actif — famille comprise. Passer
  // par ici plutôt que par le gestionnaire de clic est ce qui fait que les
  // tuiles de l'écran d'accueil, qui sautent directement sur « Alertes » ou
  // « Fournisseurs », changent aussi de famille au lieu de laisser la barre
  // allumée sur la précédente.
  synchroniserNavigation(t);

  // Panneaux : TOUS sont adressés, l'actif visible et les autres masqués.
  // Un panneau oublié n'est plus possible — c'est ce qui faisait apparaître
  // « Fournisseurs » sous la liste au premier chargement.
  const vis = panesVisibles(t);
  for (const [pane, visible] of Object.entries(vis)) {
    const n = document.getElementById(pane);
    if (n) n.style.display = visible ? "block" : "none";
  }

  // Barre de filtres : masquée là où elle n'était que décorative et trompeuse.
  const filtres = document.querySelector(".filters");
  if (filtres) filtres.style.display = filtresVisibles(t) ? "" : "none";

  const tri = triDeLOnglet(t);
  if (tri) SORT = tri;
  else if (SORT === "echeance") SORT = "date";

  // Chargement propre à l'onglet.
  if (t === "today" || t === "canaux") { chargerEcranAccueil(); return; }
  if (t === "pilotage") { chargerEcranPilotage(); return; }
  if (t === "frais") { chargerEcranFrais(); return; }
  if (t === "remgros") { chargerEcranRemiseGrossiste(); return; }
  if (t === "marques") { chargerEcranEtiquetage(); return; }
  if (t === "cout") { document.getElementById("coutSearch").focus(); return; }
  if (t === "four") { loadFournisseurs(); return; }
  if (t === "col")  { loadCollecteurs(); return; }
  if (t === "rem")  { loadRemises(); return; }
  reload();
}

/** Écran des frais : rechargé après chaque écriture, pour que le solde affiché
 *  soit toujours celui de la base et jamais une valeur reconstruite côté client. */
async function chargerEcranFrais(){
  const racine = document.getElementById("frais");
  if (!racine) return;
  racine.replaceChildren(Object.assign(document.createElement("div"),
    { className: "muted", textContent: "Chargement de vos frais…" }));
  try {
    const donnees = await chargerFrais(api);
    rendreFrais(racine, donnees, api, chargerEcranFrais);
  } catch (e) {
    racine.replaceChildren(Object.assign(document.createElement("div"),
      { className: "err", textContent: "Impossible de charger : " + e.message }));
  }
}

/** Écran « Remise grossiste » : période et grossiste vivent dans l'état du module ;
 *  seul leur changement redemande des données, les barèmes se recalculent sur place. */
async function chargerEcranRemiseGrossiste(){
  const racine = document.getElementById("remisegrossiste");
  if (!racine) return;
  racine.replaceChildren(Object.assign(document.createElement("div"),
    { className: "muted", textContent: "Lecture de vos achats grossiste…" }));
  try {
    const donnees = await chargerRemiseGrossiste(api);
    rendreRemiseGrossiste(racine, donnees, chargerEcranRemiseGrossiste);
  } catch (e) {
    racine.replaceChildren(Object.assign(document.createElement("div"),
      { className: "err", textContent: "Impossible de lire vos achats : " + e.message }));
  }
}

/** Écran de pilotage : la fenêtre du curseur et la période de marge réalisée sont conservées entre deux visites. */
let FENETRE_PILOTAGE = 90;
let PERIODE_RENTABILITE = null;
async function chargerEcranPilotage(){
  const racine = document.getElementById("pilotage");
  if (!racine) return;
  racine.replaceChildren(Object.assign(document.createElement("div"),
    { className: "muted", textContent: "Calcul de votre marge…" }));
  try {
    const donnees = await chargerPilotage(api, FENETRE_PILOTAGE, PERIODE_RENTABILITE);
    // `boutonGarder` en 4e argument : le taux de marge est gardable, mais
    // `entreeMarge` refuse de le produire sans sa couverture.
    rendrePilotage(racine, donnees, (jours) => {
      FENETRE_PILOTAGE = jours;
      chargerEcranPilotage();
    }, boutonGarder, (periode) => {
      PERIODE_RENTABILITE = periode;
      chargerEcranPilotage();
    });
  } catch (e) {
    racine.replaceChildren(Object.assign(document.createElement("div"),
      { className: "err", textContent: "Impossible de calculer la marge : " + e.message }));
  }
}

/** Écran d'accueil : chaque source est indépendante, une panne n'efface pas le reste. */
async function chargerEcranAccueil(){
  const racine = document.getElementById(TAB === "canaux" ? "canaux" : "accueil");
  if (!racine) return;
  racine.replaceChildren(Object.assign(document.createElement("div"),
    { className: "muted", textContent: "Analyse de vos canaux…" }));
  const donnees = await chargerAccueil(api);
  // Même chargement, DEUX vues : « Aujourd'hui » montre ce qui demande une
  // décision, « Canaux » montre l'état de chaque tuyau. Avant, les deux onglets
  // rendaient le même écran.
  rendreAccueil(racine, donnees, setTab, TAB === "canaux" ? "canaux" : "decisions", boutonGarder);
}

/**
 * Écran d'étiquetage des marques.
 *
 * Après CHAQUE décision, la carte disparaît sur place plutôt que de recharger la
 * page entière : recharger renverrait en haut de liste à chaque validation, et
 * cet écran s'utilise en rafale — vingt décisions d'affilée. On ne va rechercher
 * une page qu'une fois la liste vide.
 */
async function chargerEcranEtiquetage(){
  const racine = document.getElementById("etiquetage");
  if (!racine) return;
  racine.replaceChildren(Object.assign(document.createElement("div"),
    { className: "muted", textContent: "Chargement des groupes à étiqueter…" }));

  let donnees;
  try {
    donnees = await chargerEtiquetage(api);
  } catch (e) {
    racine.replaceChildren(Object.assign(document.createElement("div"),
      { className: "err", textContent: "Impossible de charger les groupes : " + (e?.message || e) }));
    return;
  }

  const decisions = [];
  rendreEtiquetage(racine, { ...donnees, decisions }, async (decision, carte) => {
    try {
      await api("etiquetage_decider", decision);
    } catch (e) {
      // On REND la main plutôt que d'avaler : une décision perdue en silence
      // se re-poserait plus tard, et il la reprendrait sans savoir qu'elle
      // avait déjà été prise.
      //
      // Sans `carte`, l'appel vient du dépliant code-par-code, qui gère son
      // propre affichage d'erreur : on RELANCE pour qu'il puisse le faire. Une
      // erreur avalée ici lui laisserait croire que l'enregistrement a réussi.
      if (!carte) throw e;
      carte.classList.remove("etq-envoi");
      for (const b of carte.querySelectorAll("button")) b.disabled = false;
      const etat = carte.querySelector(".etq-etat");
      if (etat) { etat.textContent = "Échec — la décision n'est pas enregistrée. Réessayez."; etat.className = "etq-etat err"; }
      return;
    }
    decisions.push(decision);
    // Décision partielle (dépliant) : la carte RESTE, il y a encore des codes à
    // traiter dedans. C'est le dépliant qui grise ce qui est fait.
    if (!carte) return;
    carte.remove();
    if (!racine.querySelector(".etq-carte")) chargerEcranEtiquetage();
  }, (cle) => chargerCodesGroupe(api, cle));
}

function filterParams(){
  return {
    q:document.getElementById("q").value,
    fournisseur:document.getElementById("fournisseur").value,
    source:document.getElementById("source").value,
    from:document.getElementById("from").value,
    to:document.getElementById("to").value,
  };
}

let _t;
function debouncedLoad(){ clearTimeout(_t); _t=setTimeout(reload,300); }

// reload = repart de zéro (offset 0, remplace)
async function reload(){
  if(TAB==="ana"){ loadStats(); return; }
  if(TAB==="rem"){ loadRemises(); return; }
  if(TAB==="four"){ loadFournisseurs(); return; }
  if(TAB==="col"){ loadCollecteurs(); return; }
  OFFSET=0; LOADED=0;
  const tbody=document.getElementById("rows");
  tbody.replaceChildren(emptyRow("Chargement…"));
  document.getElementById("more").style.display="none";
  document.getElementById("cnt").textContent="";
  try{
    const data = await api("list", {...filterParams(), sort:SORT, unpaid_only: TAB==="ech", ech_future: TAB==="ech", limit:PAGE, offset:0});
    if(data.labos) fillLabos(data.labos);
    if(LIEN_LABO && data.labos){
      const texte = LIEN_LABO; LIEN_LABO = null;
      if(appliquerLaboDuLien(texte, data.labos)){ reload(); return; }
    }
    const {totaux} = data;
    document.getElementById("stN").textContent = totaux.n.toLocaleString("fr-FR");
    document.getElementById("stHT").textContent = eur(totaux.ht);
    document.getElementById("stTTC").textContent = eur(totaux.ttc);
    majVentilation(totaux);
    render(data.rows, true);
    LOADED = data.rows.length; OFFSET = data.rows.length;
    updatePager(data.has_more, totaux.n);
    syncSortArrows();
  }catch(err){
    tbody.replaceChildren(emptyRow("Erreur : "+err.message));
  }
}

// loadMore = page suivante, append
async function loadMore(){
  const btn=document.getElementById("more");
  btn.disabled=true; btn.textContent="Chargement…";
  try{
    const data = await api("list", {...filterParams(), sort:SORT, unpaid_only: TAB==="ech", ech_future: TAB==="ech", limit:PAGE, offset:OFFSET});
    render(data.rows, false);
    LOADED += data.rows.length; OFFSET += data.rows.length;
    updatePager(data.has_more, data.totaux.n);
  }catch(err){ toast("Impossible de charger la suite : "+err.message, "err"); }
  finally{ btn.disabled=false; btn.textContent="Charger plus"; }
}

function updatePager(hasMore, total){
  document.getElementById("cnt").textContent = `${LOADED.toLocaleString("fr-FR")} affichées sur ${total.toLocaleString("fr-FR")}`;
  document.getElementById("more").style.display = hasMore? "inline-block":"none";
}

function setSort(c){
  if(c==="echeance"){ SORT="echeance"; }
  else if(SORT===c){ /* déjà trié, no-op (tri serveur unidirectionnel) */ SORT=c; }
  else SORT=c;
  reload();
}
function syncSortArrows(){
  document.querySelectorAll(".arr").forEach(a=>{ a.textContent = a.dataset.c===SORT ? "▼" : ""; });
}

function fillLabos(labos){
  const sel=document.getElementById("fournisseur");
  const cur=sel.value;
  if(sel.dataset.filled===String(labos.length)) return;
  sel.replaceChildren();
  const all=document.createElement("option"); all.value=""; all.textContent="Tous"; sel.appendChild(all);
  for(const l of labos){
    const o=document.createElement("option"); o.value=l.fournisseur;
    o.textContent=`${l.fournisseur} (${l.n})`; sel.appendChild(o);
  }
  sel.value=cur; sel.dataset.filled=String(labos.length);
}

// echClass → lib/format.js (corrigé : une facture échéant AUJOURD'HUI était
// classée « dépassée », le calcul comparant minuit UTC à l'instant local).

// Affiche « dont N pièces de charges » sous les totaux, et rien du tout s'il n'y
// en a pas. Le calcul est dans lib/nature.js et testé là-bas ; ici, uniquement
// le DOM.
function majVentilation(totaux){
  const el = document.getElementById("ventilNature");
  if(!el) return;
  const v = ventilation(totaux);
  if(!v){ el.style.display="none"; el.textContent=""; return; }
  el.textContent = "dont "+eur(v.htCharge)+" HT de charges ("+v.nCharge+" "+v.piece
                 + ") — hors des chiffres d'achat, mais bien dues";
  el.style.display="";
}

function render(rows, replace){
  const tbody=document.getElementById("rows");
  if(replace && !rows.length){ tbody.replaceChildren(emptyRow("Aucune facture.")); return; }
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const tr=document.createElement("tr");
    const tdF=document.createElement("td");
    // Nom principal = laboratoire si connu, sinon grossiste/fournisseur
    const strong=document.createElement("strong"); strong.textContent=r.labo||r.fournisseur||"—"; tdF.appendChild(strong);
    if(r.type_document && r.type_document!=="facture"){
      const pill=document.createElement("span"); pill.className="pill"; pill.textContent=r.type_document; tdF.appendChild(pill);
    }
    // Nature économique : ce qui n'est PAS de la marchandise se voit, parce que
    // ces lignes sont sorties des chiffres d'achat sans quitter la liste ni
    // l'échéancier. Sans marque visible, l'écart entre le total de la liste et
    // le total des achats serait inexplicable à l'œil.
    const nat = pastilleNature(r);
    if(nat){
      const pn=document.createElement("span"); pn.className=nat.classe; pn.textContent=nat.texte; tdF.appendChild(pn);
    }
    if(r.source){ const sp=document.createElement("span"); sp.className="pill src"; sp.textContent=SRC_LABEL[r.source]||r.source; tdF.appendChild(sp); }
    // Sous-ligne « via grossiste » quand le labo est connu et différent
    if(r.labo && r.fournisseur && r.labo!==r.fournisseur){
      const via=document.createElement("div"); via.className="muted"; via.style.fontSize="11px";
      via.textContent="via "+r.fournisseur; tdF.appendChild(via);
    }
    tr.appendChild(tdF);
    tr.appendChild(cell(r.num_facture||"—"));
    tr.appendChild(cell(fdate(r.date_facture)));
    tr.appendChild(cell(fdate(r.date_echeance), echClass(r.date_echeance)));
    tr.appendChild(cell(r.mode_reglement||"—","hide"));
    tr.appendChild(cell(eur2(r.montant_ht),"num"));
    tr.appendChild(cell(eur2(r.montant_tva),"num hide"));
    const tdT=document.createElement("td"); tdT.className="num";
    const b=document.createElement("strong"); b.textContent=eur2(r.montant_ttc); tdT.appendChild(b); tr.appendChild(tdT);
    const tdS=document.createElement("td");
    const lbl=document.createElement("label"); lbl.className="paidbox";
    const cb=document.createElement("input"); cb.type="checkbox"; cb.checked=!!r.paid;
    const span=document.createElement("span"); span.className="paidlbl";
    const setLbl=v=>{ span.textContent=v?"Payée":"À payer"; span.classList.toggle("on",v); };
    setLbl(!!r.paid);
    cb.addEventListener("change",()=>setPaid(r.id, cb.checked, setLbl, r.montant_ttc, cb));
    lbl.appendChild(cb); lbl.appendChild(span); tdS.appendChild(lbl); tr.appendChild(tdS);
    const tdP=document.createElement("td");
    if(r.ref_fichier){
      const btn=document.createElement("button"); btn.className="lnk"; btn.textContent="Voir";
      btn.addEventListener("click",()=>openPdf(r.id, btn)); tdP.appendChild(btn);
    } else { tdP.textContent="—"; tdP.className="muted"; }
    tr.appendChild(tdP);
    frag.appendChild(tr);
  }
  if(replace) tbody.replaceChildren(frag); else tbody.appendChild(frag);
}

async function setPaid(id, paid, setLbl, ttc, caseACocher){
  try{
    await api("set_paid",{id, paid});
    setLbl(paid);
  }catch(err){
    // CORRIGÉ : le navigateur a DÉJÀ basculé la case avant que la requête parte.
    // Sans remise en état, un échec laissait la case cochée et le libellé sur
    // « à payer » — trois états contradictoires à l'écran après un seul clic, et
    // le titulaire croyait avoir enregistré un paiement qui n'existe pas.
    if (caseACocher) caseACocher.checked = !paid;
    setLbl(!paid);
    toast("Le paiement n'a pas pu être enregistré : " + err.message, "err");
  }
}

async function openPdf(id, bouton){
  // CORRIGÉ : window.open APRÈS un await sort de la pile du geste utilisateur,
  // et Safari comme Firefox le bloquent en silence — le bouton « Voir » ne
  // faisait alors strictement rien, sans message. On ouvre l'onglet TOUT DE
  // SUITE, puis on y navigue une fois l'URL signée obtenue.
  const onglet = window.open("", "_blank");
  if (bouton) bouton.disabled = true;
  try{
    const {url} = await api("sign",{id});
    if (!url) throw new Error("aucun fichier associé à cette facture");
    if (onglet) onglet.location.replace(url);
    else window.location.assign(url);   // popup bloquée : on navigue à la place
  }catch(err){
    if (onglet) onglet.close();
    // 404 = la référence en base ne correspond à aucun fichier dans le stockage (mesuré le
    // 2026-09-19 sur 973 factures DigiPharmacie) : ce n'est pas une panne, on le dit.
    const message = err && err.statut === 404
      ? "aucun fichier dans le stockage pour cette facture — le PDF est dans HubPharma ou dans la boîte mail"
      : err.message;
    toast("PDF indisponible : " + message, "err");
  }finally{
    if (bouton) bouton.disabled = false;
  }
}

// ---- Analyse ----
async function loadStats(){
  document.getElementById("echCards").replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"Chargement…"}));
  document.getElementById("byMonth").replaceChildren();
  document.getElementById("topF").replaceChildren();
  document.getElementById("bySource").replaceChildren();
  try{
    const s = await api("stats", filterParams());
    renderEcheancier(s.a_venir);
    renderByMonth(s.by_month);
    renderTopF(s.top_fournisseurs);
    renderBySource(s.by_source);
  }catch(err){
    // CORRIGÉ : le message d'erreur n'atterrissait que dans « echCards ». Les
    // trois autres panneaux restaient VIDES, sans rien dire — ce qui se lit comme
    // « aucune donnée sur cette période », alors que la requête a échoué. Chaque
    // zone qu'on a mise en attente doit être sortie de l'attente.
    const msg = "Impossible de charger : " + err.message;
    for (const id of ["echCards","byMonth","topF","bySource"]) {
      const n = document.getElementById(id);
      if (n) n.replaceChildren(Object.assign(document.createElement("div"),{className:"err",textContent:msg}));
    }
  }
}

function ecCard(cls, label, amount, sub){
  const d=document.createElement("div"); d.className="ec"+(cls?(" "+cls):"");
  const v=document.createElement("div"); v.className="v"; v.textContent=eur(amount);
  const l=document.createElement("div"); l.className="l"; l.textContent=label;
  d.appendChild(v); d.appendChild(l);
  if(sub){ const s=document.createElement("div"); s.className="s"; s.textContent=sub; d.appendChild(s); }
  return d;
}
function renderEcheancier(e){
  const c=document.getElementById("echCards");
  if(!e){ c.replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"—"})); return; }
  const vide = !(Number(e.semaine)||Number(e.mois)||Number(e.apres));
  if(vide){ c.replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"Aucune échéance à venir."})); return; }
  // Les trois tranches sont DISJOINTES et le disent : « Sous 30 jours » laissait
  // croire que la tranche englobait la semaine, alors que son montant démarre à
  // J+7. Deux cartes semblaient alors se contredire.
  const nb = (n) => (n ? `${n} facture${n > 1 ? "s" : ""}` : "");
  // Un bouton par TRANCHE : c'est la tranche qu'on garde, pas le montant du
  // moment. `entreeEcheance` la redit disjointe sur la fiche, sinon deux lignes
  // sembleraient s'y contredire — le défaut déjà corrigé à l'écran.
  const tranche = (carte, cle, libelle, montant, sub) => {
    const ent = entreeEcheance(cle, libelle, montant, sub);
    if (ent) carte.appendChild(boutonGarder(ent));
    return carte;
  };
  c.replaceChildren(
    tranche(ecCard("semaine","Sous 7 jours", e.semaine, nb(e.n_semaine)),
            "semaine", "Sous 7 jours", e.semaine, nb(e.n_semaine)),
    tranche(ecCard("","De 7 à 30 jours", e.mois, nb(e.n_mois)),
            "mois", "De 7 à 30 jours", e.mois, nb(e.n_mois)),
    tranche(ecCard("","Au-delà de 30 jours", e.apres, nb(e.n_apres)),
            "apres", "Au-delà de 30 jours", e.apres, nb(e.n_apres))
  );
}
function renderByMonth(rows){
  const box=document.getElementById("byMonth");
  if(!rows || !rows.length){ box.replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"Aucune donnée."})); return; }
  const max=Math.max(...rows.map(r=>Number(r.ttc)||0),1);
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const ttc=Number(r.ttc)||0;
    const bar=document.createElement("div"); bar.className="bar";
    const tip=document.createElement("div"); tip.className="tip"; tip.textContent=`${moisLbl(r.mois)} · ${eur(ttc)} · ${r.n} fact.`;
    const col=document.createElement("div"); col.className="col"; col.style.height=Math.round(ttc/max*140)+"px";
    const xl=document.createElement("div"); xl.className="xl"; xl.textContent=moisLbl(r.mois);
    bar.appendChild(tip); bar.appendChild(col); bar.appendChild(xl);
    frag.appendChild(bar);
  }
  box.replaceChildren(frag);
  box.scrollLeft = box.scrollWidth;   // dernier mois visible
}
function renderTopF(rows){
  const box=document.getElementById("topF");
  if(!rows || !rows.length){ box.replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"Aucune donnée."})); return; }
  const max=Math.max(...rows.map(r=>Number(r.ttc)||0),1);
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const ttc=Number(r.ttc)||0;
    const row=document.createElement("div"); row.className="hbar";
    const nm=document.createElement("div"); nm.className="nm"; nm.textContent=r.fournisseur; nm.title=r.fournisseur;
    const track=document.createElement("div"); track.className="track";
    const fill=document.createElement("div"); fill.className="fill"; fill.style.width=Math.round(ttc/max*100)+"%"; track.appendChild(fill);
    const amt=document.createElement("div"); amt.className="amt"; amt.textContent=`${eur(ttc)} · ${r.n}`;
    row.appendChild(nm); row.appendChild(track); row.appendChild(amt);
    frag.appendChild(row);
  }
  box.replaceChildren(frag);
}
function renderBySource(rows){
  const box=document.getElementById("bySource");
  if(!rows || !rows.length){ box.replaceChildren(Object.assign(document.createElement("div"),{className:"muted",textContent:"Aucune donnée."})); return; }
  const max=Math.max(...rows.map(r=>Number(r.ttc)||0),1);
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const ttc=Number(r.ttc)||0;
    const row=document.createElement("div"); row.className="hbar";
    const nm=document.createElement("div"); nm.className="nm"; nm.textContent=SRC_LABEL[r.source]||r.source||"—";
    const track=document.createElement("div"); track.className="track";
    const fill=document.createElement("div"); fill.className="fill"; fill.style.width=Math.round(ttc/max*100)+"%"; track.appendChild(fill);
    const amt=document.createElement("div"); amt.className="amt"; amt.textContent=`${eur(ttc)} · ${r.n}`;
    row.appendChild(nm); row.appendChild(track); row.appendChild(amt);
    frag.appendChild(row);
  }
  box.replaceChildren(frag);
}

// ---- Alertes conditions (remise ↓ / prix catalogue ↑ / frais de port) ----
let ALERTS=[];
const TYPE_LABEL={remise:"Remise ↓", prix:"Prix ↑", port:"Frais port"};
async function loadRemises(){
  const tbody=document.getElementById("remRows");
  tbody.replaceChildren(emptyRow("Chargement…"));
  try{
    const minRemise=Number(document.getElementById("remDelta").value)||1;
    const minPrix=Number(document.getElementById("remPrix").value)||1;
    const oppo=document.getElementById("remOppo").checked;
    const {rows, snaps, total, tronque}=await api("alertes",{min_remise:minRemise, min_prix_pct:minPrix, q:document.getElementById("q").value, opposable_only:oppo});
    ALERTS=rows||[];
    const info=document.getElementById("remInfo");
    const nR=ALERTS.filter(a=>a.type==="remise").length, nP=ALERTS.filter(a=>a.type==="prix").length, nF=ALERTS.filter(a=>a.type==="port").length;
    let base="";
    if(snaps && snaps.length>=2){ const a=snaps[snaps.length-2], b=snaps[snaps.length-1];
      base=` Socle : snapshots ${fdate(a.snapshot_date)} → ${fdate(b.snapshot_date)}.`; }
    else if(snaps && snaps.length===1){ base=` Un seul snapshot (${fdate(snaps[0].snapshot_date)}).`; }
    // Le nombre affiché était le PLAFOND de la requête, présenté comme un total :
    // « 300 alertes » pouvait aussi bien vouloir dire 300 que 3 000. Tant qu'on
    // ne le disait pas, aucune décision prise sur cette liste ne valait rien.
    const tete = tronque
      ? `${ALERTS.length} alerte(s) affichée(s) sur ${total} — liste tronquée`
      : `${ALERTS.length} alerte(s)`;
    // Ce qu'il y a à récupérer, tous fournisseurs confondus : c'est le chiffre
    // qui décide si ça vaut la peine d'écrire.
    const somme=ALERTS.reduce((s,a)=>s+(Number(a.impact_eur)||0),0);
    const nOppo=ALERTS.filter(a=>a.opposable===true).length;
    info.textContent=`${tete} · ${nR} remise, ${nP} prix, ${nF} frais de port · ${nOppo} opposable(s)`
      + (somme>0?` · ${eur2(somme)} de surcoût chiffré`:"")+`.${base}`;
    renderAlertesFilter();
  }catch(err){
    document.getElementById("remRows").replaceChildren(emptyRow("Erreur : "+err.message));
  }
}
function renderAlertesFilter(){
  const t=document.getElementById("remType").value;
  renderAlertes(t? ALERTS.filter(a=>a.type===t) : ALERTS);
}
function renderAlertes(rows){
  const tbody=document.getElementById("remRows");
  if(!rows || !rows.length){ tbody.replaceChildren(emptyRow("Aucune alerte sur ces seuils.")); return; }
  const fmt=(v,u)=> v==null?"—" : (u==="€"? eur2(v) : Number(v).toLocaleString("fr-FR",{maximumFractionDigits:1})+(u==="%"?" %":(u==="pts"?" %":"")));
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const tr=document.createElement("tr");
    const tdT=document.createElement("td");
    const b=document.createElement("span"); b.className="tbadge "+r.type; b.textContent=TYPE_LABEL[r.type]||r.type; tdT.appendChild(b); tr.appendChild(tdT);
    tr.appendChild(cell(r.laboratoire||r.fournisseur||"—"));
    tr.appendChild(cell(r.designation||"—"));
    tr.appendChild(cell(fmt(r.valeur_avant, r.unite),"num"));
    tr.appendChild(cell(fmt(r.valeur_apres, r.unite),"num"));
    const tdD=document.createElement("td"); tdD.className="num "+(r.type==="remise"?"delta-down":"delta-up");
    const d=Number(r.delta); tdD.textContent=(d>0?"+":"")+d.toLocaleString("fr-FR",{maximumFractionDigits:1})+(r.unite==="€"?" €":(r.unite==="%"?" %":" pts")); tr.appendChild(tdD);
    // Le surcoût sur la livraison concernée. Absent quand la comparaison passe par
    // un snapshot catalogue : il n'y a alors aucune quantité achetée sur laquelle
    // le calculer, et une estimation inventée serait la pire des réponses.
    tr.appendChild(cell(r.impact_eur!=null?eur2(r.impact_eur):"—","num"));
    // Les PIÈCES plutôt que les seules dates : c'est ce que le fournisseur ouvrira.
    const pieces=r.num_facture_apres
      ? `${r.num_facture_avant?r.num_facture_avant+" → ":""}${r.num_facture_apres} (${fdate(r.date_avant)} → ${fdate(r.date_apres)})`
      : `${fdate(r.date_avant)} → ${fdate(r.date_apres)} — snapshot, non opposable`;
    tr.appendChild(cell(pieces,"hide"));
    // « Garder » n'apparaît QUE sur les lignes opposables : `entreeAlerte` rend
    // null pour les autres. Un bouton présent sur une ligne qui ne peut pas
    // fonder de réclamation inviterait à l'envoyer quand même.
    const tdG=document.createElement("td");
    const ent=entreeAlerte(r);
    if(ent) tdG.appendChild(boutonGarder(ent));
    tr.appendChild(tdG);
    frag.appendChild(tr);
  }
  tbody.replaceChildren(frag);
}

// ---- Collecteurs unifiés (actifs / inactifs) ----
const COL_LABEL={mail_etik:"Mail facture@", gsa:"GSA", cerp:"CERP", ocp:"OCP", digipharmacie:"DigiPharmacie", hub:"HubPharma", sy:"SY Cegedim", upsa:"UPSA"};
const INACTIVE_CAP=90;             // limite d'affichage (affiner par recherche)
const _testing=new Set();          // slugs dont le test de connexion est en cours
let _credPoll=null, _toastT=null, _colT=null;

function fEmpty(txt){ return Object.assign(document.createElement("div"),{className:"muted",textContent:txt}); }
function phLogo(name){
  const d=document.createElement("div"); d.className="flogo ph"; d.textContent=(name||"?").trim().charAt(0).toUpperCase(); return d;
}
function fStat(v,l){
  const d=document.createElement("div"); d.className="fst";
  const sv=document.createElement("div"); sv.className="sv"; sv.textContent=v;
  const sl=document.createElement("div"); sl.className="sl"; sl.textContent=l;
  d.appendChild(sv); d.appendChild(sl); return d;
}
function toast(msg, kind){          // kind: "ok" | "err" | undefined
  const t=document.getElementById("toast");
  t.textContent=msg; t.className=(kind||"")+" show";
  clearTimeout(_toastT);
  _toastT=setTimeout(()=>{ t.className=t.className.replace("show","").trim(); }, kind==="err"?7000:4500);
}
let _debCol; function debouncedCol(){ clearTimeout(_colT); _colT=setTimeout(()=>{ RANGE.inactif=""; renderInactiveZone(); },200); }

// ---- Onglet Fournisseurs : cartes labo/grossiste/groupement ⨝ historique d'achat ----
let _fournT; function debouncedFourn(){ clearTimeout(_fournT); _fournT=setTimeout(loadFournisseurs,300); }
const CAT_BADGE={ labo:{cls:"edi",txt:"Labo"}, grossiste:{cls:"wait",txt:"Grossiste"}, groupement:{cls:"none",txt:"Groupement"} };
async function loadFournisseurs(){
  const q=document.getElementById("fournSearch").value;
  const categorie=document.getElementById("fournCat").value;
  const only_mine=document.getElementById("fournMine").checked;
  document.getElementById("fournGrid").replaceChildren(fEmpty("Chargement…"));
  try{
    const d=await api("fournisseurs",{q, categorie, only_mine});
    renderFournCards(d);
  }catch(err){ document.getElementById("fournGrid").replaceChildren(fEmpty("Erreur : "+err.message)); }
}
function renderFournCards(d){
  const grid=document.getElementById("fournGrid");
  const rows=d.rows||[];
  document.getElementById("fournInfo").textContent = `· ${d.mine||0} avec achats / ${d.total||0} fiches`;
  if(!rows.length){ grid.replaceChildren(fEmpty("Aucun fournisseur.")); return; }
  const frag=document.createDocumentFragment();
  for(const r of rows){
    const item={...r, label:r.display_name};
    const badge=CAT_BADGE[r.categorie]||{cls:"none",txt:r.categorie};
    frag.appendChild(collecteurCard(item,{ cardCls: r.n!=null?"mine":"st-none", badge, relance:r.relance }));
  }
  grid.replaceChildren(frag);
}

// statut d'un collecteur ACTIF → classe carte + badge + macaron éventuel
// Le barème est parti dans lib/collecteur-statut.js le 2026-08-13 : il devenait
// faux dès qu'un collecteur jamais configuré s'affichait (il annonçait
// « Configuré » à SY, qui n'a aucun identifiant). Il y est testé.
const activeStatus = c => statutCollecteur(c, _testing.has(c.slug));

// État client : on charge TOUT (q:"") une fois, puis filtrage tranches/recherche au navigateur.
let COL={actifs:[],inactifs:[],noway:[]};
let RANGE={actif:"",inactif:""};   // "" = 1re tranche par défaut
let NOWAY_OPEN=false;
let COL_SORT="alpha";              // "alpha" (défaut) | "ca" (Quick Win)

// D\u00e9grad\u00e9 charte (p\u00e9trole \u2192 corail) appliqu\u00e9 verticalement le long de la liste.
// accentAt + palette → lib/tri.js (corrigé : les valeurs n'étaient NI la charte v4
// NI le teal legacy, sous un commentaire affirmant appliquer la charte).
// Badge cat\u00e9gorie (r\u00e9utilise les pastilles sbadge existantes).
const COL_CAT_BADGE={ labo:{cls:"edi",txt:"Labo"}, grossiste:{cls:"wait",txt:"Grossiste"},
  groupement:{cls:"none",txt:"Groupement"}, outil:{cls:"off",txt:"Outil"},
  hors_pharma:{cls:"off",txt:"Hors-pharma"}, pharmacie:{cls:"none",txt:"Pharmacie"},
  formation:{cls:"off",txt:"Formation"} };
// clé de tri alphabétique : ignore les déterminants de tête (le, la, les, l', du, de…)
// sortKey → lib/tri.js
function setColSort(mode){
  COL_SORT=mode; RANGE.inactif="";
  document.getElementById("sortAlpha").classList.toggle("on",mode==="alpha");
  document.getElementById("sortCA").classList.toggle("on",mode==="ca");
  renderInactiveZone();
}

async function loadCollecteurs(){
  document.getElementById("activeGrid").replaceChildren(fEmpty("Chargement…"));
  document.getElementById("inactiveGrid").replaceChildren(fEmpty("Chargement…"));
  try{
    const d=await api("collecteurs_view",{q:""});
    COL.actifs=d.actifs||[]; COL.inactifs=d.inactifs||[]; COL.noway=d.noway||[];
    renderColZones();
  }catch(err){
    // Même correction : « inactiveGrid » restait sur « Chargement… » POUR TOUJOURS,
    // puisque seul « activeGrid » recevait le message d'erreur.
    const msg = "Impossible de charger : " + err.message;
    document.getElementById("activeGrid").replaceChildren(fEmpty(msg));
    document.getElementById("inactiveGrid").replaceChildren(fEmpty(msg));
  }
  try{
    const {jobs, sources}=await api("collect_status");
    // La map vient du catalogue chargé au-dessus, dans un try INDÉPENDANT : s'il
    // a échoué, COL.actifs est vide, la map l'est aussi, et separerSources rend
    // `sait:false` — la liste s'affiche alors à plat, et non toute repliée.
    renderColBtns(sources||[], new Map(COL.actifs.map(c=>[c.slug, c.connecteur_pret])));
    renderColJobs(jobs||[]);
  }catch(err){ document.getElementById("colRows").replaceChildren(emptyRow("Erreur : "+err.message)); }
}
function renderColZones(){ renderActiveZone(); renderInactiveZone(); renderNowayZone(); }

// Découpe une liste TRIÉE en tranches ~équilibrées étiquetées par lettres (A–C…).
// buildRanges → lib/tri.js (durci : ne lève plus sur une entrée absente)
function renderRangeNav(el, ranges, zoneKey){
  el.replaceChildren();
  if(ranges.length<2){ el.style.display="none"; return; }   // 0 ou 1 tranche = inutile
  el.style.display="flex";
  for(const r of ranges){
    const b=document.createElement("button");
    b.className="rchip"+(RANGE[zoneKey]===r.key?" on":"");
    b.textContent=r.label;
    b.addEventListener("click",()=>{ RANGE[zoneKey]=r.key; (zoneKey==="actif"?renderActiveZone:renderInactiveZone)(); });
    el.appendChild(b);
  }
}

// Carte commune (logo + nom + badge/macaron + stats + portail + action)
function collecteurCard(item, opts){
  const c=document.createElement("div"); c.className="fcard "+(opts.cardCls||"st-none");
  if(opts.accent){ c.style.borderTop="4px solid "+opts.accent.solid; c.style.background=`linear-gradient(180deg, ${opts.accent.faint}, #fff 55%)`; }
  const head=document.createElement("div"); head.className="fhead";
  if(item.logo_url){
    const img=document.createElement("img"); img.className="flogo"; img.src=item.logo_url; img.alt=item.label; img.loading="lazy";
    img.onerror=()=>img.replaceWith(phLogo(item.label));
    head.appendChild(img);
  } else head.appendChild(phLogo(item.label));
  const ti=document.createElement("div");
  const nm=document.createElement("div"); nm.className="fnm"; nm.textContent=item.label; ti.appendChild(nm);
  const badges=document.createElement("div"); badges.style.cssText="display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-top:3px";
  if(opts.badge){ const b=document.createElement("span"); b.className="sbadge "+opts.badge.cls; b.style.fontSize="11px"; b.textContent=opts.badge.txt; badges.appendChild(b); }
  if(opts.macaron){ const m=document.createElement("span"); m.className="macaron"; m.textContent="⏳ "+opts.macaron; badges.appendChild(m); }
  if(opts.relance){ const r=document.createElement("span"); r.className="sbadge err"; r.style.fontSize="11px"; r.textContent="À relancer"; badges.appendChild(r); }
  ti.appendChild(badges); head.appendChild(ti); c.appendChild(head);
  if(opts.sub){ const s=document.createElement("div"); s.className="ftag"; s.textContent=opts.sub; c.appendChild(s); }
  if(item.n!=null){
    const st=document.createElement("div"); st.className="fstats";
    st.appendChild(fStat(eur(item.ttc),"acheté"));
    st.appendChild(fStat(Number(item.n).toLocaleString("fr-FR"),"factures"));
    if(item.freq_days) st.appendChild(fStat("~"+Math.round(item.freq_days)+" j","cadence"));
    c.appendChild(st);
    if(item.last_d){ const sub=document.createElement("div"); sub.className="ftag"; sub.textContent="Dernière facture : "+fdate(item.last_d); c.appendChild(sub); }
  }
  // coin supérieur droit : action icône (ex. dégager 🚽)
  if(opts.corner){
    const cb=document.createElement("button"); cb.className="cornerbtn"; cb.textContent=opts.corner.emoji;
    cb.setAttribute("data-tip",opts.corner.title); cb.setAttribute("aria-label",opts.corner.title);
    cb.addEventListener("click",e=>{ e.stopPropagation(); opts.corner.fn(item); });
    c.appendChild(cb);
  }
  // pied : Portail (coin inf. gauche) + action primaire (coin inf. droit), même taille
  const foot=document.createElement("div"); foot.className="ffoot";
  if(item.portail_url){
    const a=document.createElement("a"); a.className="fportal foot-btn"; a.href=item.portail_url; a.target="_blank"; a.rel="noopener"; a.textContent="Portail ↗"; foot.appendChild(a);
  } else { const sp=document.createElement("span"); sp.className="fnoportal"; sp.textContent=item.by_mail?"Par mail":"—"; foot.appendChild(sp); }
  if(opts.primary){
    const btn=document.createElement("button"); btn.className="btn btn-ghost btn-sm foot-btn"; btn.textContent=opts.primary.label;
    btn.addEventListener("click",()=>opts.primary.fn(item)); foot.appendChild(btn);
  }
  // « Garder » dans le pied, à côté de l'action principale : la carte sert
  // AUSSI aux collecteurs (configurer un accès), et l'entrée n'a de sens que
  // pour un fournisseur chez qui on a acheté. `entreeFournisseur` refuse une
  // carte sans identité, donc rien à filtrer ici.
  const ent = entreeFournisseur(item);
  if (ent) foot.appendChild(boutonGarder(ent));
  c.appendChild(foot); return c;
}

function activeCardOpts(c){
  const s=activeStatus(c);
  const action=actionCollecteur(c);
  return { cardCls:s.card, badge:{cls:s.cls,txt:s.txt}, macaron:s.macaron,
           sub:sousTitreCollecteur(c),
           primary: action ? {label:action, fn:openCred} : null };
}

function renderActiveZone(){
  const grid=document.getElementById("activeGrid");
  let items=COL.actifs;
  const ranges=buildRanges(items);
  renderRangeNav(document.getElementById("actifNavTop"),ranges,"actif");
  renderRangeNav(document.getElementById("actifNavBot"),ranges,"actif");
  if(ranges.length){ const sel=ranges.find(r=>r.key===RANGE.actif)||ranges[0]; RANGE.actif=sel.key; items=sel.items; }
  if(!items.length){ grid.replaceChildren(fEmpty("Aucun collecteur actif. Configurez-en un dans « inactifs ».")); return; }
  const frag=document.createDocumentFragment();
  for(const c of items) frag.appendChild(collecteurCard(c,activeCardOpts(c)));
  grid.replaceChildren(frag);
}

function renderInactiveZone(){
  const grid=document.getElementById("inactiveGrid");
  const term=_norm(document.getElementById("colSearch").value);
  const navTop=document.getElementById("inactNavTop"), navBot=document.getElementById("inactNavBot");
  let items;
  if(COL_SORT==="ca"){   // Quick Win : que ceux qui pèsent (avec historique), du + lourd au + léger
    items=COL.inactifs.filter(l=>l.n!=null).sort((a,b)=>(b.ttc||0)-(a.ttc||0));
  } else {               // défaut : alphabétique (déterminants ignorés)
    items=[...COL.inactifs].sort((a,b)=>sortKey(a.label).localeCompare(sortKey(b.label),"fr"));
  }
  if(term) items=items.filter(l=>_norm(l.label).includes(term));
  document.getElementById("inactiveInfo").textContent =
    "· " + (COL_SORT==="ca" ? items.length+" avec achats" : COL.inactifs.length+" portails");
  if(COL_SORT==="alpha" && !term){   // tranches de lettres : mode alpha sans recherche uniquement
    const ranges=buildRanges(items);
    renderRangeNav(navTop,ranges,"inactif"); renderRangeNav(navBot,ranges,"inactif");
    if(ranges.length){ const sel=ranges.find(r=>r.key===RANGE.inactif)||ranges[0]; RANGE.inactif=sel.key; items=sel.items; }
  } else { renderRangeNav(navTop,[],"inactif"); renderRangeNav(navBot,[],"inactif"); }
  if(!items.length){ grid.replaceChildren(fEmpty(term?"Aucun résultat.":"Aucun portail.")); return; }
  const frag=document.createDocumentFragment();
  items.forEach((l,idx)=> frag.appendChild(collecteurCard(l,{ cardCls:"st-none", relance:l.relance,
      badge:COL_CAT_BADGE[l.categorie], accent:accentAt(idx,items.length),
      primary:{label:"Configurer", fn:openCred},
      corner:{emoji:"🚽", title:"Partenaire à dégager pour plus de lisibilité", fn:it=>degager(it.slug,it.label)} })));
  grid.replaceChildren(frag);
}

function renderNowayZone(){
  const grid=document.getElementById("nowayGrid"); const n=COL.noway.length;
  document.getElementById("nowayPanel").style.display = n? "block":"none";
  document.getElementById("nowayInfo").textContent = n?`· ${n}`:"";
  document.getElementById("nowayToggle").textContent = NOWAY_OPEN?"Réduire":"Voir";
  grid.style.display = (NOWAY_OPEN && n)? "grid":"none";
  if(!(NOWAY_OPEN && n)) return;
  const frag=document.createDocumentFragment();
  COL.noway.forEach((l,idx)=> frag.appendChild(collecteurCard(l,{ cardCls:"st-none",
      badge:COL_CAT_BADGE[l.categorie], accent:accentAt(idx,COL.noway.length),
      primary:{label:"Remettre", fn:it=>remettre(it.slug,it.label)} })));
  grid.replaceChildren(frag);
}
function toggleNoway(){ NOWAY_OPEN=!NOWAY_OPEN; renderNowayZone(); }

async function degager(slug,label){
  try{ await api("collecteur_masquer",{slug});
    const i=COL.inactifs.findIndex(x=>x.slug===slug);
    if(i>=0){ COL.noway.unshift(COL.inactifs[i]); COL.inactifs.splice(i,1); }
    renderColZones(); toast(`« ${label} » dégagé → No Way!`);
  }catch(e){ toast("Erreur : "+e.message,"err"); }
}
async function remettre(slug,label){
  try{ await api("collecteur_demasquer",{slug});
    const i=COL.noway.findIndex(x=>x.slug===slug);
    if(i>=0){ COL.inactifs.push(COL.noway[i]); COL.noway.splice(i,1);
      COL.inactifs.sort((a,b)=>((a.ttc==null)-(b.ttc==null))||((b.ttc||0)-(a.ttc||0))||a.label.localeCompare(b.label)); }
    renderColZones(); toast(`« ${label} » remis en inactifs`);
  }catch(e){ toast("Erreur : "+e.message,"err"); }
}

// Surveille le statut après saisie des creds (test+sync lancé côté poste).
//
// CORRIGÉ : il n'existait qu'UN SEUL handle de minuterie pour tous les
// collecteurs. Configurer un second connecteur tuait la surveillance du premier,
// dont l'entrée dans `_testing` n'était alors jamais retirée — sa carte affichait
// « Test en cours… » indéfiniment, jusqu'au rechargement complet de la page.
// Une minuterie PAR collecteur, indexée par son identifiant.
const _polls = new Map();
function pollCredTest(slug, label){
  const precedent = _polls.get(slug);
  if (precedent) clearInterval(precedent);

  let n = 0;
  const arreter = () => {
    const h = _polls.get(slug);
    if (h) clearInterval(h);
    _polls.delete(slug);
    _testing.delete(slug);
  };

  const handle = setInterval(async ()=>{
    n++;
    try{
      const d=await api("collecteurs_view",{q:""});
      COL.actifs=d.actifs||[]; COL.inactifs=d.inactifs||[]; COL.noway=d.noway||[]; renderColZones();
      const c=(d.actifs||[]).find(x=>x.slug===slug);
      if(c && c.statut==="ok"){ arreter(); loadCollecteurs(); toast(`✅ ${label} : connexion réussie, factures synchronisées.`,"ok"); }
      else if(c && c.statut==="erreur_auth"){ arreter(); loadCollecteurs(); toast(`⛔ ${label} : identifiants refusés. ${c.derniere_erreur||"Vérifiez login/mot de passe."}`,"err"); }
    }catch(_){}
    // Au bout de ~3 min sans verdict, on abandonne EN LE DISANT : un état
    // « test en cours » qui s'éteint sans un mot laisse croire qu'il tourne encore.
    if(n>=30){ arreter(); renderColZones(); toast(`⏱ ${label} : pas de réponse du poste après 3 minutes. Vérifiez que le collecteur tourne.`,"err"); }
  }, 6000);
  _polls.set(slug, handle);
}

function openCred(item){
  const form=document.getElementById("credForm");
  form.dataset.slug=item.slug; form.dataset.label=item.label;
  document.getElementById("credTitle").textContent="Identifiants · "+item.label;
  document.getElementById("credSub").textContent = item.portail_url || ("Portail "+item.label);
  document.getElementById("credErr").textContent="";
  const login=document.getElementById("credLogin");
  login.value=item.login||"";
  login.placeholder="Identifiant " + (item.label||"du portail");   // dynamique (était figé « GSA »)
  const pwSet=document.getElementById("credPwSet"), pw=document.getElementById("credPw");
  if(item.has_secret){ pwSet.style.display="flex"; pw.style.display="none"; pw.value=""; pw.dataset.touched=""; }
  else { pwSet.style.display="none"; pw.style.display="block"; pw.value=""; pw.dataset.touched="1"; }
  document.getElementById("credModalBg").classList.add("show");
  setTimeout(()=>document.getElementById("credLogin").focus(),50);
}
function enablePw(){
  const pw=document.getElementById("credPw");
  document.getElementById("credPwSet").style.display="none";
  pw.style.display="block"; pw.dataset.touched="1"; pw.value=""; pw.focus();
}
function closeCred(){ document.getElementById("credModalBg").classList.remove("show"); }
async function saveCred(e){
  e.preventDefault();
  const form=document.getElementById("credForm");
  const slug=form.dataset.slug, label=form.dataset.label||slug;
  const login=document.getElementById("credLogin").value.trim();
  const pwEl=document.getElementById("credPw");
  const errEl=document.getElementById("credErr"); errEl.textContent="";
  if(!login){ errEl.textContent="Identifiant requis."; return; }
  const params={collecteur:slug, login};
  if(pwEl.dataset.touched){ if(!pwEl.value){ errEl.textContent="Mot de passe requis."; return; } params.password=pwEl.value; }
  const btn=document.getElementById("credSave"); btn.disabled=true; btn.textContent="Enregistrement…";
  try{
    const r=await api("creds_upsert", params);
    pwEl.value=""; closeCred();
    if(r.test_queued){
      _testing.add(slug);
      toast(`🔌 ${label} : test de connexion lancé — le statut se met à jour automatiquement.`);
      pollCredTest(slug, label);
    } else if(r.connecteur_pret===false){
      toast(`✅ ${label} : identifiants enregistrés. Le connecteur arrive bientôt — vous serez notifié quand la collecte sera active.`,"ok");
    }
    loadCollecteurs();
  }catch(err){ errEl.textContent="Erreur : "+err.message; }
  finally{ btn.disabled=false; btn.textContent="Enregistrer"; }
}
// Deux groupes, et non une liste à plat de trente-et-un boutons : « Siphonner
// Mail facture@ » s'y trouvait au même rang que vingt-huit sources qui répondent
// « non câblé », et restait introuvable pour cette seule raison (2026-10-29).
// Le critère est LU dans la base (`connecteur_pret`, servi par collecteurs_view)
// et non recopié en dur : cette colonne bouge — SY valait faux en août, vrai en
// octobre — et une liste figée côté front serait morte-née.
// Les boutons repliés sont rendus DÉSACTIVÉS et ne sont pas câblés du tout : un
// clic enfilait un job qui finissait en `error « non câblé »`, soit une ligne
// d'erreur dans le journal pour une action que rien ne pouvait satisfaire.
// Ne pas câbler plutôt que câbler puis bloquer : `disabled` est un état que la
// prochaine retouche de style ou de rendu peut lever sans y penser, alors que
// l'absence de gestionnaire rend l'intention structurelle. Le style existe déjà
// dans l'app (`.sbtn:disabled`, index.html) — rien d'inventé ici.
// Quand `separerSources` ne SAIT pas (map absente), tout retombe dans `pretes`
// et reste donc actionnable : on ne désactive rien sur une ignorance.
function renderColBtns(sources, pretParSlug){
  const {pretes, bientot}=separerSources(sources, pretParSlug);
  const bouton=(s, actionnable)=>{
    const b=document.createElement("button"); b.className="sbtn";
    b.textContent="💧 Siphonner "+(COL_LABEL[s]||s);
    if(actionnable){ b.addEventListener("click",()=>triggerCollect(s, b)); }
    else{ b.disabled=true; b.title="Connecteur pas encore écrit : il n'y a rien à siphonner pour cette source."; }
    return b;
  };
  const remplir=(id,liste,actionnable)=>{
    const frag=document.createDocumentFragment();
    for(const s of liste) frag.appendChild(bouton(s, actionnable));
    document.getElementById(id).replaceChildren(frag);
  };
  remplir("colBtns", pretes, true);
  remplir("colBientot", bientot, false);
  document.getElementById("colBientotWrap").style.display = bientot.length? "":"none";
  document.getElementById("colBientotInfo").textContent = bientot.length? "("+bientot.length+")":"";
  cablerColBientot(); renderColBientot();
}

// Le repli des sources pas encore câblées, sur le pattern de « No Way! ».
// Câblé par addEventListener plutôt que par un attribut inline : c'est la dette
// que `globalesHtml` trace, et elle se rembourse sur l'écran qu'on touche.
let COL_BIENTOT_OPEN=false, COL_BIENTOT_CABLE=false;
function cablerColBientot(){
  if(COL_BIENTOT_CABLE) return;          // renderColBtns est rappelé à chaque chargement
  document.getElementById("colBientotToggle")
    .addEventListener("click",()=>{ COL_BIENTOT_OPEN=!COL_BIENTOT_OPEN; renderColBientot(); });
  COL_BIENTOT_CABLE=true;
}
function renderColBientot(){
  document.getElementById("colBientot").style.display = COL_BIENTOT_OPEN? "flex":"none";
  document.getElementById("colBientotToggle").textContent = COL_BIENTOT_OPEN? "Réduire":"Voir";
}
async function triggerCollect(source, btn){
  btn.disabled=true; const old=btn.textContent; btn.textContent="Envoi…";
  try{
    const r=await api("collect",{source});
    btn.textContent = r.queued? "✓ en file" : "déjà en file";
    setTimeout(loadCollecteurs, 600);
  }catch(err){ toast("Erreur : "+err.message, "err"); btn.textContent=old; }
  finally{ setTimeout(()=>{btn.disabled=false; btn.textContent=old;}, 1500); }
}
function renderColJobs(jobs){
  const tbody=document.getElementById("colRows");
  if(!jobs.length){ tbody.replaceChildren(emptyRow("Aucune collecte enregistrée.")); return; }
  const dt=d=> d? new Date(d).toLocaleString("fr-FR") : "—";
  const frag=document.createDocumentFragment();
  for(const j of jobs){
    const tr=document.createElement("tr");
    tr.appendChild(cell(COL_LABEL[j.source]||j.source));
    const tdS=document.createElement("td"); const b=document.createElement("span"); b.className="jstatus "+j.status; b.textContent=j.status; tdS.appendChild(b); tr.appendChild(tdS);
    tr.appendChild(cell(dt(j.requested_at)+(j.requested_by?` (${j.requested_by})`:"")));
    tr.appendChild(cell(dt(j.started_at),"hide"));
    tr.appendChild(cell(dt(j.finished_at),"hide"));
    let detail=j.error||"";
    if(!detail && j.result){ try{ const r=typeof j.result==="string"?JSON.parse(j.result):j.result; detail=r.message||JSON.stringify(r); }catch(_){ detail=String(j.result); } }
    tr.appendChild(cell(detail||"—"));
    frag.appendChild(tr);
  }
  tbody.replaceChildren(frag);
}

// Le démarrage est piloté par src/main.js, qui expose d'abord les fonctions
// citées dans les attributs HTML puis ouvre la connexion.

// ── Fonctions citées dans les attributs HTML `onclick=` / `oninput=` ──────────
// Un module ES a sa propre portée : sans cette réexposition, les 35 attributs
// inline de index.html casseraient tous d'un coup. C'est une DETTE, assumée et
// tracée ici — elle se rembourse feature par feature, en remplaçant les attributs
// par des addEventListener au fur et à mesure qu'on touche à chaque écran.
export const globalesHtml = {
  setTab, reload, loadMore, setSort, setColSort,
  debouncedLoad, debouncedCout, debouncedCol, debouncedFourn,
  loadCout, loadFournisseurs, loadRemises, renderAlertesFilter,
  toggleNoway, saveCred, closeCred, enablePw,
  logout: seDeconnecter,
  etikidRedirect: () => allerVersEtikid(),
};

export { showApp };
